<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import {useAuthStore} from '~/plugins/store/auth';
import {translation, useTranslationStore} from '~/plugins/store/translations';
import {useNuxtApp} from '#app';
import MasterLayout from "~/layouts/MasterLayout.vue";
import {
  type ApiOffersResponse,
  createDefaultHouseGoods,
  createDefaultHouseInsuranceForm,
  createDefaultHouseInsuranceOfferRequest,
  createDefaultValidationHouseInsuranceFormStatus,
  type HouseGoods,
  type HouseInsuranceCompany,
  type HouseInsuranceForm,
  type HouseInsuranceOfferRequest,
  type ValidationHouseInsuranceFormStatus
} from '~/types/House';
import HouseBuildingHeightSelect from "~/components/house/HouseBuildingHeightSelect.vue";
import HouseAddressTypeTab from "~/components/house/HouseAddressTypeTab.vue";
import HouseNumberOfRooms from "~/components/house/HouseNumberOfRooms.vue";
import HouseAmountInsured from "~/components/house/HouseAmountInsured.vue";
import HouseIsWithSeismicRisk from "~/components/house/HouseIsWithSeismicRisk.vue";
import HouseIsSystemSecurityInstalled from "~/components/house/HouseIsSystemSecurityInstalled.vue";
import HouseSeismicRiskSelect from "~/components/house/HouseSeismicRiskSelect.vue";
import AddressFloor from "~/components/address/AddressFloor.vue";
import type {OcrResponse, TranslationList} from "~/types/Utils";
import {type AddressFormValidation, createDefaultAddressFormValidation} from "~/types/Address";
import {
  type ApiCompanyData,
  createDefaultApiCompanyData,
  createDefaultHouseCustomerForm,
  createDefaultHouseCustomerFormValidation,
  type HouseCustomerForm,
  type HouseCustomersFormValidation,
  type RcaDriverFormValidation
} from "~/types/Customer";
import UploadDocument from "~/components/utils/UploadDocument.vue";
import {useDocumentStore} from "~/plugins/store/document";
import {recaptcha} from "~/composables/recaptcha";
import {useReCaptcha} from "vue-recaptcha-v3";
import {useCompanyStore} from "~/plugins/store/company";
import ConfirmationDialog from "~/components/utils/ConfirmationDialog.vue";
import {useProductStore} from "~/plugins/store/product";
import {useHouseStore} from "~/plugins/store/house";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AddBasicVehicleDialog from "~/components/vehicle/AddBasicVehicleDialog.vue";
import AddExtraGoodsDialog from "~/components/house/AddExtraGoodsDialog.vue";
import HousePadSeries from "~/components/house/HousePadSeries.vue";
import HousePadNumber from "~/components/house/HousePadNumber.vue";
import type {Product} from "~/types/Product";

export default defineComponent({
  setup() {
    useHead({
      title: '24Broker | Asigurare de locuință',
      meta: [
        {
          name: 'description',
          content: 'Asigură-ți locuința împotriva riscurilor cu polițe flexibile și accesibile. Protejează-ți casa și bunurile cu 24Broker.ro, ușor și rapid.'
        }
      ],
    });

    // Utility function to introduce a delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const {$toast} = useNuxtApp();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const {$dayjs} = useNuxtApp();
    const userAlreadyExistsWithEmail = ref(false);
    const offers = ref<{ [key: number]: ApiOffersResponse[] }>({});
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const isSessionRestored = ref(false);
    const isLoadingSkeleton = ref(false);
    const isValidFromDisabled = ref(false);
    const activePanels = ref([0]);
    const houseInsuranceForm = ref<HouseInsuranceForm>(createDefaultHouseInsuranceForm());
    const houseGoodInsured = ref<HouseGoods>(createDefaultHouseGoods());
    const showAddExtraGoodsDialog = ref(false);
    const validationHouseStatus = ref<ValidationHouseInsuranceFormStatus>(createDefaultValidationHouseInsuranceFormStatus());
    const validationHouseAddressStatus = ref<AddressFormValidation>(createDefaultAddressFormValidation());
    const activeStep = ref(1);
    const isSeismicRiskRequired = computed(() => houseInsuranceForm.value.is_house_with_seismic_risk);
    const houseInsuranceOwnerForm = ref<HouseCustomerForm>(createDefaultHouseCustomerForm());
    const validationHouseCustomersStatus = ref<HouseCustomersFormValidation>({});
    const validationHouseOwnerAddressStatus = ref<AddressFormValidation>(createDefaultAddressFormValidation());
    const documentStore = useDocumentStore();
    const documentList = ref(documentStore.documentList);
    const recaptchaInstance = useReCaptcha();
    const apiCompanyData = ref<ApiCompanyData>(createDefaultApiCompanyData());
    const companySore = useCompanyStore();
    const showConfirmationDialog = ref(false);
    const messageConfirmationDialog = ref('');
    const contextConfirmationDialog = ref('');
    const identifierConfirmationDialog = ref('');
    const productStore = useProductStore();
    let houseInsuranceProduct = <Product>({});
    const houseStore = useHouseStore();
    const insuranceCompanies = ref<HouseInsuranceCompany[]>([]);
    const stepperItems = ref(
        [
          {
            title: translation(translationList.value.building_details, 'Detalii locuință'),
            rules: [() => false],
          },
          {
            title: translation(translationList.value.owner_details, 'Detalii proprietar'),
          },
          {
            title: translation(translationList.value.offer_details, 'Detalii ofertă'),
          },
          {
            title: translation(translationList.value.comparator, 'Comparator'),
          }
        ]);

    const SNAPSHOT_KEY = 'house_form_snapshot';
    const saveSnapshot = async (): Promise<void> => {
      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1500);
      const snapshot = {
        houseInsuranceForm: houseInsuranceForm.value,
        validationHouseStatus: validationHouseStatus.value,
        validationHouseAddressStatus: validationHouseAddressStatus.value,
        validationHouseCustomersStatus: validationHouseCustomersStatus.value,
        validationHouseOwnerAddressStatus: validationHouseOwnerAddressStatus.value,
      };
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      $toast.info(translation(translationList.value.session_saved, 'Sesiunea a fost salvată!'));
    };

    const restoreSnapshot = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.snapshot_restoring, 'Restaurăm sesiunea anterioară...');
      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1500);
      const savedSnapshot = localStorage.getItem(SNAPSHOT_KEY);
      if (savedSnapshot) {
        const snapshot = JSON.parse(savedSnapshot);
        houseInsuranceForm.value = snapshot.houseInsuranceForm;
        if (houseInsuranceForm.value.users) {
          houseInsuranceOwnerForm.value = houseInsuranceForm.value.users[0];
        }
        validationHouseStatus.value = snapshot.validationHouseStatus;
        validationHouseAddressStatus.value = snapshot.validationHouseAddressStatus;
        validationHouseCustomersStatus.value = snapshot.validationHouseCustomersStatus;
        validationHouseOwnerAddressStatus.value = snapshot.validationHouseOwnerAddressStatus;
        isSessionRestored.value = true;
        $toast.info(translation(translationList.value.session_restored, 'Sesiunea a fost restaurată!'));
      }
      showLoadingDialog.value = false;
    };

    const now = $dayjs();
    houseInsuranceForm.value.valid_from = now.add(5, 'day').format('YYYY-MM-DD'); //it must be at least 5 days in the future

    const scrollToTop = async () => {
      const {$scrollToTop} = useNuxtApp();
      $scrollToTop();
    }

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      insuranceCompanies.value = await houseStore.getInsuranceCompanies();

      const savedSnapshot = localStorage.getItem(SNAPSHOT_KEY);
      if (savedSnapshot) {
        showConfirmationDialog.value = true;
        identifierConfirmationDialog.value = 'restore-session';
        messageConfirmationDialog.value = translation(translationList.value.restore_session,
            'Restaurare sesiune anterioară');
      }

      if (user.value) {
        fillUserData();
      }

      if (!productStore.productStatuses.is_house_insurance_available) {
        navigateTo('/product-unavailable');
      }

      houseInsuranceProduct = await productStore.getHouseInsuranceProduct();
    });


    const fillUserData = () => {
      if (user.value) {
        if (!validationHouseCustomersStatus.value[0]) {
          validationHouseCustomersStatus.value[0] = createDefaultHouseCustomerFormValidation();
        }
        houseInsuranceOwnerForm.value.email = user.value.email;
        validationHouseCustomersStatus.value[0].email = true;
        houseInsuranceOwnerForm.value.phone_number = user.value.phone_number;
        validationHouseCustomersStatus.value[0].phone_number = true;
      }
    };

    watch(houseGoodInsured, (newValue, oldValue) => {
      if (newValue && oldValue !== newValue && houseInsuranceForm.value.goods_list_insured) {
        const existingIndex = houseInsuranceForm.value.goods_list_insured.findIndex(
            (item) => item.goods_type?.id === newValue.goods_type?.id
        );

        //TODO de ce sa facem update daca exista? de ce nu adaug un nou obiect?
        if (existingIndex !== -1) {
          // Update existing entry
          houseInsuranceForm.value.goods_list_insured[existingIndex] = {...newValue};
        } else {
          // Add new entry
          houseInsuranceForm.value.goods_list_insured.push({...newValue});
        }
      }
    });

    const handleValidationHouseStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationHouseStatus.value[field] = isValid;
    };
    const handleValidationHouseAddressStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationHouseAddressStatus.value[field] = isValid;
    };

    const handleValidationHouseOwnerAddressStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationHouseOwnerAddressStatus.value[field] = isValid;
    };

    const handleValidationCustomerStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      const [baseField, index] = field.split('-');

      if (index !== undefined) {
        const numericIndex = Number(index);
        if (isNaN(numericIndex)) {
          console.error('Invalid index:', index);
          return;
        }

        if (!validationHouseCustomersStatus.value[numericIndex]) {
          validationHouseCustomersStatus.value[numericIndex] = createDefaultHouseCustomerFormValidation();
        }

        const driverValidation = validationHouseCustomersStatus.value[numericIndex] as RcaDriverFormValidation;
        driverValidation[baseField] = isValid;

      }
    };

    const handleUserLoggedIn = async () => {
      const newUser = authStore.getUser();
      $toast.success(translation(translationList.value.successfully_logged_in, 'Te-ai logat cu succes!'));
      accessToken = authStore.getAccessToken();
      userAlreadyExistsWithEmail.value = false;
      user.value = newUser;
      fillUserData();
    };

    watch(() => houseInsuranceOwnerForm.value.cif_code, async (newValue, oldValue) => {
      if (newValue && newValue.length && newValue !== oldValue && !houseInsuranceOwnerForm.value.company_name) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = translation(translationList.value.company_details_searching, 'Cautam detaliile companiei...');
        const token = await recaptcha(recaptchaInstance);
        apiCompanyData.value = await companySore.fetchCompanyData(newValue, token);
        await clearCompanyData();
        await fillCompanyData();
        showLoadingDialog.value = false;
      }
    });

    const clearCompanyData = async () => {
      houseInsuranceOwnerForm.value.company_name = null;
      houseInsuranceOwnerForm.value.caen_code = null;
    };

    const fillCompanyData = () => {
      if (apiCompanyData.value) {
        if (apiCompanyData.value.company_name && !houseInsuranceOwnerForm.value.company_name) {
          houseInsuranceOwnerForm.value.company_name = apiCompanyData.value.company_name;
        }
        if (apiCompanyData.value.caen_code && !houseInsuranceOwnerForm.value.caen_code) {
          houseInsuranceOwnerForm.value.caen_code = apiCompanyData.value.caen_code;
        }
      }
    };

    const handleOcrResponse = async ({response, documentId}: { response: OcrResponse, documentId: number }) => {
      switch (documentId) {
        case documentList.value.identity_card_id:
          houseInsuranceOwnerForm.value.last_name = response.last_name ? response.last_name : null;
          houseInsuranceOwnerForm.value.first_name = response.first_name ? response.first_name : null;
          houseInsuranceOwnerForm.value.cnp = response.cnp ? response.cnp : null;
          houseInsuranceOwnerForm.value.series_ci = response.series_ci ? response.series_ci : null;
          houseInsuranceOwnerForm.value.nr_ci = response.nr_ci ? response.nr_ci : null;
          break;
      }
    };

    const isBuildingTypeHouse = ref(false);
    watch(() => houseInsuranceForm.value.building_type, async (newValue, oldValue) => {
      if (newValue !== oldValue && newValue && newValue.name === 'Casa') {
        validationHouseStatus.value.identifier = false;
        isBuildingTypeHouse.value = true;
      } else {
        validationHouseStatus.value.identifier = true;
        isBuildingTypeHouse.value = false;
      }
    });

    watch(() => houseInsuranceForm.value.policy_type, async (newValue, oldValue) => {
      if (newValue && newValue.name === 'Facultativa') {
        validationHouseStatus.value.pad_number = false;
        validationHouseStatus.value.pad_series = false;
        isValidFromDisabled.value = false;
      } else {
        const now = $dayjs();
        houseInsuranceForm.value.valid_from = now.add(5, 'day').format('YYYY-MM-DD'); //it must be at least 5 days in the future
        isValidFromDisabled.value = true;

        houseInsuranceForm.value.pad_number = null;
        validationHouseStatus.value.pad_number = true;
        houseInsuranceForm.value.pad_series = null;
        validationHouseStatus.value.pad_series = true;
      }
    });

    watch(() => houseInsuranceOwnerForm.value.email, async (newValue, oldValue) => {
      if (newValue !== oldValue && !user.value && newValue && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {

        const token = await recaptcha(recaptchaInstance);
        userAlreadyExistsWithEmail.value = newValue ? await authStore.checkEmailExists(newValue, token) : false;
      }
    });

    const validateStep = async (step: number) => {
      switch (step) {
        case 1:
          await scrollToTop();
          showLoadingDialog.value = true;
          messageLoadingDialog.value = translation(translationList.value.building_details_validating, 'Validăm datele clădirii...');

          const fieldsToValidateStep1 = [
            'building_type',
            'year_of_construction',
            'building_height',
            'address_type',
            'resistance_structure',
            'number_of_rooms',
            'surface',
            'insured_sum_type',
            'amount_insured',
            'is_house_with_seismic_risk',
            'is_system_security_installed',
            'gdpr_agreement'
          ];

          if (isBuildingTypeHouse.value) {
            fieldsToValidateStep1.push('identifier');
          }

          if (isSeismicRiskRequired.value) {
            fieldsToValidateStep1.push('seismic_risk');
          }

          let allValidStep1 = true;
          fieldsToValidateStep1.forEach(field => {
            if (!validationHouseStatus.value[field]) {
              console.log('Validation failed for field: ', field);
              window.dispatchEvent(new CustomEvent('validate-house', {detail: field}));
              allValidStep1 = false;
            }
          });

          const fieldsToValidateStep2 = [
            'county',
            'city',
            'street_type',
            'street',
            'street_number',
            'postal_code'
          ];

          let allValidStep2 = true;
          fieldsToValidateStep2.forEach(field => {
            if (!validationHouseAddressStatus.value[field]) {
              console.log('Validation failed for field: ', field);
              window.dispatchEvent(new CustomEvent('validate-house', {detail: field}));
              allValidStep2 = false;
            }
          });

          if (!allValidStep1 || !allValidStep2) {
            $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
            showLoadingDialog.value = false;
            return;
          }

          if (!isSessionRestored.value) {
            messageConfirmationDialog.value = translation(translationList.value.same_address_as_house, 'Completare automata adresa');
            contextConfirmationDialog.value = translation(translationList.value.same_address_as_house_helper, 'Daca adresa proprietarului este aceeasi cu adresa locuintei, puteti completa automat adresa proprietarului cu adresa locuintei.');

            showConfirmationDialog.value = true;
            identifierConfirmationDialog.value = 'same-address';
          }

          activeStep.value = 2;
          showLoadingDialog.value = false;
          break;

        case 2:
          await scrollToTop();
          showLoadingDialog.value = true;
          messageLoadingDialog.value = translation(translationList.value.customer_details_validating, 'Validăm datele introduse...');
          const ownerValidation = validationHouseCustomersStatus.value[0]; //only validate owner
          let allValidStep3 = false;
          if (ownerValidation) {

            const fieldsToValidateStep3 = houseInsuranceOwnerForm.value.is_legal
                ? ['cif_code', 'company_name', 'caen_code', 'email', 'phone_number']
                : ['first_name', 'last_name', 'cnp', 'series_ci', 'nr_ci', 'email', 'phone_number'];

            allValidStep3 = true;
            fieldsToValidateStep3.forEach(field => {
              if (!ownerValidation[field]) {
                console.log('Validation failed for field: ', field);
                window.dispatchEvent(new CustomEvent('validate-customer', {detail: `${field}-0`}));
                allValidStep3 = false;
              }
            });
          }

          const fieldsToValidateStep4 = [
            'county',
            'city',
            'street_type',
            'street',
            'street_number',
            'postal_code'
          ];

          let allValidStep4 = true;
          fieldsToValidateStep4.forEach(field => {
            if (!validationHouseOwnerAddressStatus.value[field]) {
              console.log('Validation failed for field: ', field);
              window.dispatchEvent(new CustomEvent('validate-house', {detail: field}));
              allValidStep4 = false;
            }
          });

          if (!allValidStep3 || !allValidStep4) {
            $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
            showLoadingDialog.value = false;
            return;
          }

          houseInsuranceForm.value.users = [houseInsuranceOwnerForm.value]; //add owner object to the customers array

          activeStep.value = 3; // Proceed to the next step
          showLoadingDialog.value = false;
          break;

        case 3:
          await scrollToTop();
          showLoadingDialog.value = true;
          messageLoadingDialog.value = translation(translationList.value.policy_details_validating, 'Validăm datele introduse...');

          const fieldsToValidateStep5 = [
            'policy_type',
            'valid_from'
          ];

          const fieldsToValidateIfPidStep5 = [
            'pad_number',
            'pad_series'
          ];

          let allValidStep5 = true;
          fieldsToValidateStep5.forEach(field => {
            if (!validationHouseStatus.value[field]) {
              console.log('Validation failed for field: ', field);
              window.dispatchEvent(new CustomEvent('validate-house', {detail: field}));
              allValidStep5 = false;
            }
          });

          if (houseInsuranceForm.value.policy_type?.name === 'Facultativa') {
            fieldsToValidateIfPidStep5.forEach(field => {
              if (!validationHouseStatus.value[field]) {
                console.log('Validation failed for field: ', field);
                window.dispatchEvent(new CustomEvent('validate-house', {detail: field}));
                allValidStep5 = false;
              }
            });
          }

          if (!allValidStep5) {
            $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
            showLoadingDialog.value = false;
            return;
          }

          messageLoadingDialog.value = translation(translationList.value.snapshot_saving, 'Salvăm sesiunea...');
          await saveSnapshot();

          //create user if needed
          if (!user.value) {
            const token = await recaptcha(recaptchaInstance);
            const tmpAuthResponse = await authStore.storeUserFromProductPage(houseInsuranceOwnerForm.value, houseInsuranceProduct.id, token);
            if (tmpAuthResponse) {
              accessToken = tmpAuthResponse.access_token;
              userAlreadyExistsWithEmail.value = false;
              user.value = tmpAuthResponse.user;
              fillUserData();
            }
          }

          if (accessToken) {
            messageLoadingDialog.value = translation(translationList.value.offers_fetching, 'Obținem ofertele...');
            if (!await fetchOffers()) {
              $toast.error(translation(translationList.value.error_retry, 'A apărut o eroare. Te rugăm să încerci din nou.'));
              showLoadingDialog.value = false;
              return;
            }
          } else {
            //if no accessToken is available, show an error message, don't proceed
            $toast.error(translation(translationList.value.error_retry, 'A apărut o eroare. Te rugăm să încerci din nou.'));
            showLoadingDialog.value = false;
            return;
          }

          activeStep.value = 4;
          showLoadingDialog.value = false;

          break;
      }
    };


    const fetchOffers = async () => {
      if (!accessToken) {
        return false;
      }
      houseInsuranceForm.value.token = await recaptcha(recaptchaInstance);
      const response = await houseStore.storeHouseInsurance(houseInsuranceForm.value, <string>accessToken);
      if (response && typeof response.id !== 'undefined') {
        houseInsuranceForm.value.house_insurance_detail_id = response.id;
      } else {
        return false;
      }

      isLoadingSkeleton.value = true;

      let houseInsuranceOfferRequest = <HouseInsuranceOfferRequest>createDefaultHouseInsuranceOfferRequest();
      houseInsuranceOfferRequest.house_insurance_detail_id = houseInsuranceForm.value.house_insurance_detail_id;
      houseInsuranceOfferRequest.valid_from = houseInsuranceForm.value.valid_from;
      houseInsuranceOfferRequest.policy_type = houseInsuranceForm.value.policy_type;
      houseInsuranceOfferRequest.pad_number = houseInsuranceForm.value.pad_number;
      houseInsuranceOfferRequest.pad_series = houseInsuranceForm.value.pad_series;

      const promises = insuranceCompanies.value.map(async (company: HouseInsuranceCompany) => {
        if (company && company.id) {
          const request = { ...houseInsuranceOfferRequest, insurance_company: company };
          try {
            request.token = await recaptcha(recaptchaInstance);
            const tmpOffers = await houseStore.fetchOffer(request, <string>accessToken);
            if (tmpOffers) {
              const validOffers = tmpOffers.filter((offerGroup: any) =>
                  offerGroup.some((offer: any) => !offer.has_error)
              );
              if (validOffers.length > 0) {
                offers.value[company.id] = Array.isArray(validOffers) ? validOffers : [validOffers];
              }
            }
          } catch (error) {
            console.error(`Error fetching offers for company ${company.id}:`, error);
          }
        }
      });

      await Promise.all(promises);
      isLoadingSkeleton.value = false;
      return true;
    };

    const handleAddExtraGoodsDialogResponse = () => {
      showAddExtraGoodsDialog.value = false;
    };

    const handleConfirmationDialogResponse = async ({response, identifier}: {
      response: boolean,
      identifier: string
    }) => {
      if (response) {
        switch (identifier) {
          case 'restore-session':
            await restoreSnapshot();
            break;
          case 'same-address':
            houseInsuranceOwnerForm.value.address = {...houseInsuranceForm.value.address};
            break;
          default:
            break;
        }
      } else {
        localStorage.removeItem(SNAPSHOT_KEY);
      }
      contextConfirmationDialog.value = '';
      showConfirmationDialog.value = false;
    };

    const removeGood = (index: number) => {
      if (houseInsuranceForm.value.goods_list_insured) {
        houseInsuranceForm.value.goods_list_insured.splice(index, 1);
      }
    };

    // Funcția pentru a calcula cel mai mic `amount_to_be_payed`
    const getLowestAmount = (insurerOffers: any) => {
      let minAmount = Infinity;

      insurerOffers.forEach((offerGroup: any) => {
        // Calculează suma totală a valorilor din offerGroup
        const groupTotal = offerGroup.reduce((sum: number, offer: any) => sum + parseFloat(offer.amount_to_be_payed), 0).toFixed(2);

        // Compară totalul grupului cu valoarea minimă curentă
        if (groupTotal < minAmount) {
          minAmount = groupTotal;
        }
      });

      return minAmount !== Infinity ? minAmount : 0;
    };


    // Funcția pentru a calcula cel mai mare `amount_to_be_payed`
    const getHighestAmount = (insurerOffers: any) => {
      let maxAmount = -Infinity;

      insurerOffers.forEach((offerGroup: any) => {
        // Calculează suma totală a valorilor din offerGroup
        const groupTotal = offerGroup.reduce((sum: number, offer: any) => sum + parseFloat(offer.amount_to_be_payed), 0).toFixed(2);

        // Compară totalul grupului cu valoarea maximă curentă
        if (groupTotal > maxAmount) {
          maxAmount = groupTotal;
        }
      });

      return maxAmount !== -Infinity ? maxAmount : 0;
    };


    const getOffersLength = (insurerOffers: any) => {
      if (!insurerOffers || !Array.isArray(insurerOffers)) {
        return 0;
      }

      // Returnează numărul de grupuri de oferte
      return insurerOffers.length;
    };

    const backToStep = (step: number) => {
      if (step === 3) {
        offers.value = {};
      }
      activeStep.value = step;
    };

    return {
      translationList,
      translation,
      userAlreadyExistsWithEmail,
      handleUserLoggedIn,
      user,
      offers,
      showLoadingDialog,
      messageLoadingDialog,
      houseInsuranceForm,
      houseGoodInsured,
      showAddExtraGoodsDialog,
      handleValidationHouseStatus,
      handleValidationHouseAddressStatus,
      handleValidationHouseOwnerAddressStatus,
      validationHouseAddressStatus,
      handleValidationCustomerStatus,
      validateStep,
      activeStep,
      isSeismicRiskRequired,
      houseInsuranceOwnerForm,
      documentList,
      showConfirmationDialog,
      messageConfirmationDialog,
      contextConfirmationDialog,
      identifierConfirmationDialog,
      handleConfirmationDialogResponse,
      stepperItems,
      activePanels,
      isLoadingSkeleton,
      handleAddExtraGoodsDialogResponse,
      removeGood,
      getLowestAmount,
      getHighestAmount,
      getOffersLength,
      backToStep,
      isValidFromDisabled,
      handleOcrResponse,
      isBuildingTypeHouse
    };
  },
  components: {
    HousePadNumber,
    HousePadSeries,
    AddExtraGoodsDialog,
    AddBasicVehicleDialog,
    LoadingDialog,
    ConfirmationDialog,
    UploadDocument,
    AddressFloor,
    HouseSeismicRiskSelect,
    HouseIsSystemSecurityInstalled,
    HouseIsWithSeismicRisk,
    HouseAmountInsured,
    HouseNumberOfRooms,
    HouseBuildingHeightSelect,
    HouseAddressTypeTab,
    MasterLayout,
  }
});
</script>

<template>
  <master-layout class="house">
    <v-container class="content-container background-gradient">
      <v-container fluid class="component-container-1400mw">
        <v-container class="centered-horizontally">
          <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
          <auth-already-exists-login-dialog
              :email="houseInsuranceOwnerForm.email"
              :user-already-exists-with-email="userAlreadyExistsWithEmail"
              @loggedIn="handleUserLoggedIn"
          >
          </auth-already-exists-login-dialog>
          <confirmation-dialog
              :identifier="identifierConfirmationDialog"
              :show-dialog="showConfirmationDialog"
              :context="contextConfirmationDialog"
              :message="messageConfirmationDialog"
              @response="handleConfirmationDialogResponse"
          ></confirmation-dialog>
          <add-extra-goods-dialog
              :show-dialog="showAddExtraGoodsDialog"
              v-model="houseGoodInsured"
              @response="handleAddExtraGoodsDialogResponse"
          ></add-extra-goods-dialog>

          <v-stepper
              tile
              min-height="500"
              rounded="0"
              elevation="0"
              :items="stepperItems"
              class="house-stepper"
              selected-class="active"
              v-model="activeStep"
              :hide-actions="true"
          >
            <template v-slot:item.1>
              <div>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6">
                    <house-building-type-tab v-model="houseInsuranceForm.building_type"></house-building-type-tab>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center" v-if="isBuildingTypeHouse">
                  <v-col cols="12" sm="12" md="6">
                    <house-identifier v-model="houseInsuranceForm.identifier"
                                      @validation-house-status="handleValidationHouseStatus"></house-identifier>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.building_details, 'Detalii clădire') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.building_details_helper, 'Selectează anul în care a fost construită clădirea și înălțimea acesteia.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="6">
                    <house-year-of-constructure-select v-model="houseInsuranceForm.year_of_construction"
                                                       @validation-house-status="handleValidationHouseStatus"></house-year-of-constructure-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <house-building-height-select v-model="houseInsuranceForm.building_height"
                                                  @validation-house-status="handleValidationHouseStatus"></house-building-height-select>
                  </v-col>
                </v-row>

                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6">
                    <house-address-type-tab v-model="houseInsuranceForm.address_type"></house-address-type-tab>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.about_building_details, 'Detalii despre locuință') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.about_building_details_helper, 'Selectează tipul de structură, completează numărul de camere și specifică suprafața locuinței.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="4">
                    <house-resistance-structure-select v-model="houseInsuranceForm.resistance_structure"
                                                       @validation-house-status="handleValidationHouseStatus"></house-resistance-structure-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <house-number-of-rooms v-model="houseInsuranceForm.number_of_rooms"
                                           @validation-house-status="handleValidationHouseStatus"></house-number-of-rooms>
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <house-surface v-model="houseInsuranceForm.surface"
                                   @validation-house-status="handleValidationHouseStatus"></house-surface>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.insured_details, 'Tipul și suma asigurată') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.insured_details_helper, 'Alege tipul de sumă asigurată și completează suma în euro.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="6">
                    <house-insured-sum-type-select v-model="houseInsuranceForm.insured_sum_type"
                                                   @validation-house-status="handleValidationHouseStatus"></house-insured-sum-type-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <house-amount-insured v-model="houseInsuranceForm.amount_insured"
                                          @validation-house-status="handleValidationHouseStatus"></house-amount-insured>
                  </v-col>
                </v-row>

                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.add_extra_good, 'Asigură un bun suplimentar') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.add_extra_good_helper, 'Protejează un obiect important în plus, adăugându-l la asigurarea ta.')
                      }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex  justify-end centered-on-mobile">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="showAddExtraGoodsDialog = true"
                    >{{ translation(translationList.add_goods, 'Doresc sa asigur bunuri extra') }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row
                    v-if="houseInsuranceForm.goods_list_insured && houseInsuranceForm.goods_list_insured.length"
                    class="mb-10"
                >
                  <v-col cols="12" sm="12" md="6">
                    <v-list bg-color="primary-light" rounded="0">
                      <v-list-item>
                        <v-list-item-title class="fs-18">
                          <v-row class="d-flex align-center" no-gutters>
                            <v-col class="my-1" cols="6" sm="6" md="6">
                              <b>{{ translation(translationList.good_type, 'Tip bun') }}</b>
                            </v-col>
                            <v-col class="my-1 d-flex justify-end" cols="6" sm="6" md="6">
                              <b>{{ translation(translationList.insured_sum, 'Suma asigurata') }}</b>
                            </v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list-item
                          @click="removeGood(index)"
                          class="goods-list-item"
                          v-for="(good, index) in houseInsuranceForm.goods_list_insured"
                          :key="index"
                          :value="index"
                      >
                        <v-list-item-title class="goods-list-item-title fs-18">
                          <v-row class="d-flex align-center" no-gutters>
                            {{ index + 1 }}. {{ good.goods_type?.name }}
                            <v-spacer></v-spacer>
                            {{ good.amount_insured }} EURO
                            <v-icon
                                size="small"
                                class="delete-icon"
                                @click="removeGood(index)"
                            >
                              mdi-trash-can
                            </v-icon>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list-item>
                        <v-list-item-title class="fs-18">
                          <v-row>
                            <v-col class="my-1" cols="6" sm="6" md="6">
                              <b>{{ translation(translationList.total, 'Total') }}:
                                {{ houseInsuranceForm.goods_list_insured.length }}</b>
                            </v-col>
                            <v-col class="my-1 d-flex justify-end" cols="6" sm="6" md="6">
                              <b>{{
                                  houseInsuranceForm.goods_list_insured.reduce((acc, item) => acc + (item.amount_insured ?? 0), 0)      }}
                                EURO</b>
                            </v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>

                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.seismic_risk, 'Risc seismic') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.seismic_risk_helper, 'Confirmă dacă locuința ta prezintă risc seismic și selectează nivelul.')
                      }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="6" :md="houseInsuranceForm.is_house_with_seismic_risk ? '3' : '6' "
                         class="d-flex justify-end left-on-mobile">
                    <house-is-with-seismic-risk v-model="houseInsuranceForm.is_house_with_seismic_risk"
                                                @validation-house-status="handleValidationHouseStatus"></house-is-with-seismic-risk>

                  </v-col>
                  <v-col v-if="houseInsuranceForm.is_house_with_seismic_risk"
                         cols="12" sm="6" :md="houseInsuranceForm.is_house_with_seismic_risk ? '3' : '6' "
                         class="d-flex justify-end centered-on-mobile">
                    <house-seismic-risk-select class="mt-6" v-model="houseInsuranceForm.seismic_risk"
                                               @validation-house-status="handleValidationHouseStatus"></house-seismic-risk-select>
                  </v-col>
                </v-row>

                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.security_system, 'Sistem de securitate') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.security_system_helper, 'Confirmă dacă locuința ta este echipată cu un sistem de securitate.')
                      }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-end left-on-mobile">
                    <house-is-system-security-installed v-model="houseInsuranceForm.is_system_security_installed"
                                                        @validation-house-status="handleValidationHouseStatus"></house-is-system-security-installed>
                  </v-col>
                </v-row>

                <div>
                  <v-row>
                    <v-col cols="12">
                      <h3 class="font-weight-bold">
                        {{ translation(translationList.house_address_data, 'Adresa locuintei') }}</h3>
                      <p class="font-weight-regular">
                        {{
                          translation(translationList.house_address_data_helper, 'Completați cu adresa exactă a locuinței')
                        }}
                      </p>
                    </v-col>
                  </v-row>
                  <v-row class="my-3">
                    <v-col cols="12" sm="12" md="6">
                      <address-county-select v-model="houseInsuranceForm.address.county"
                                             @validation-address-status="handleValidationHouseAddressStatus"></address-county-select>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <address-city-select
                          v-model="houseInsuranceForm.address.city"
                          @validation-address-status="handleValidationHouseAddressStatus"
                          :county-id="houseInsuranceForm.address.county?.id"
                      ></address-city-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="4">
                      <address-street-type-select v-model="houseInsuranceForm.address.street_type"
                                                  @validation-address-status="handleValidationHouseAddressStatus"></address-street-type-select>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-street v-model="houseInsuranceForm.address.street"
                                      @validation-address-status="handleValidationHouseAddressStatus"></address-street>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-street-number v-model="houseInsuranceForm.address.street_number"
                                             @validation-address-status="handleValidationHouseAddressStatus"></address-street-number>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="4">
                      <address-building v-model="houseInsuranceForm.address.building"
                                        @validation-address-status="handleValidationHouseAddressStatus"></address-building>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-entrance v-model="houseInsuranceForm.address.entrance"
                                        @validation-address-status="handleValidationHouseAddressStatus"></address-entrance>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-floor v-model="houseInsuranceForm.address.floor"
                                     @validation-address-status="handleValidationHouseAddressStatus"></address-floor>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <address-apartment v-model="houseInsuranceForm.address.apartment"
                                         @validation-address-status="handleValidationHouseAddressStatus"></address-apartment>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <address-postal-code v-model="houseInsuranceForm.address.postal_code"
                                           @validation-address-status="handleValidationHouseAddressStatus"></address-postal-code>
                    </v-col>
                  </v-row>
                </div>

                <v-row class="my-3">
                  <v-col cols="12">
                    <house-gdpr-agreement v-model="houseInsuranceForm.gdpr_agreement"
                                         @validation-house-status="handleValidationHouseStatus"></house-gdpr-agreement>
                  </v-col>
                </v-row>

                <v-row class="mt-10 mb-15">
                  <v-col cols="12" class="d-flex align-center justify-center">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="validateStep(1)"
                    >{{ translation(translationList.continue, 'Continuă') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </template>

            <template v-slot:item.2>
              <div>
                <v-row>
                  <v-col cols="12">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.customer_type, 'Tip proprietar') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.customer_type_helper, 'Selectează dacă ești persoană fizică sau juridică.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <customer-type-tab v-model="houseInsuranceOwnerForm.is_legal"></customer-type-tab>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.customer_data, 'Date proprietar') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.customer_data_helper, 'Completați datele personale ale proprietarului.')
                      }}
                    </p>
                  </v-col>
                  <v-col v-if="!houseInsuranceOwnerForm.is_legal" cols="12" sm="12" md="6"
                         class="d-flex justify-end centered-on-mobile">
                    <upload-document
                        txtPlaceholder="Incarca buletin"
                        :documentId="documentList.identity_card_id"
                        @ocr-response="handleOcrResponse"
                    >
                    </upload-document>
                  </v-col>
                </v-row>

                <div v-show="!houseInsuranceOwnerForm.is_legal">
                  <v-row class="mt-5">
                    <v-col cols="12" sm="12" md="6">
                      <customer-last-name v-model="houseInsuranceOwnerForm.last_name"
                                          :customer-index="0"
                                          @validation-customer-status="handleValidationCustomerStatus"></customer-last-name>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <customer-first-name v-model="houseInsuranceOwnerForm.first_name"
                                           :customer-index="0"
                                           @validation-customer-status="handleValidationCustomerStatus"></customer-first-name>
                    </v-col>
                  </v-row>
                  <v-row class="mt-3" v-if="!user">
                    <v-col cols="12" sm="12" md="6">
                      <customer-email v-model="houseInsuranceOwnerForm.email"
                                      :customer-index="0"
                                      @validation-customer-status="handleValidationCustomerStatus"></customer-email>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <customer-phone-number v-model="houseInsuranceOwnerForm.phone_number"
                                             :customer-index="0"
                                             @validation-customer-status="handleValidationCustomerStatus"></customer-phone-number>
                    </v-col>
                  </v-row>
                  <v-row class="mt-3">
                    <v-col cols="12" sm="12" md="4">
                      <customer-cnp v-model="houseInsuranceOwnerForm.cnp"
                                    :customer-index="0"
                                    @validation-customer-status="handleValidationCustomerStatus"></customer-cnp>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <customer-series-ci v-model="houseInsuranceOwnerForm.series_ci"
                                          :customer-index="0"
                                          @validation-customer-status="handleValidationCustomerStatus"></customer-series-ci>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <customer-nr-ci v-model="houseInsuranceOwnerForm.nr_ci"
                                      :customer-index="0"
                                      @validation-customer-status="handleValidationCustomerStatus"></customer-nr-ci>
                    </v-col>
                  </v-row>

                  <v-row class="d-flex align-center">
                    <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                      <h3 class="font-weight-bold">
                        {{ translation(translationList.politically_exposed, 'Persoană expusă politic (PEP)') }}</h3>
                      <p class="font-weight-regular">
                        {{
                          translation(translationList.politically_exposed_helper, 'Bifează dacă ești considerat persoană expusă politic.')
                        }}
                      </p>
                    </v-col>
                    <v-col cols="12" sm="12" md="6" class="d-flex justify-end left-on-mobile">
                      <customer-is-politically-exposed v-model="houseInsuranceOwnerForm.is_politically_exposed"
                                                       @validation-customer-status="handleValidationCustomerStatus">
                      </customer-is-politically-exposed>
                    </v-col>
                  </v-row>
                </div>

                <div v-show="houseInsuranceOwnerForm.is_legal" class="mt-3">
                  <v-row class="mt-5">
                    <v-col cols="12" sm="12" md="6">
                      <customer-cif-code v-model="houseInsuranceOwnerForm.cif_code"
                                         :customer-index="0"
                                         @validation-customer-status="handleValidationCustomerStatus"></customer-cif-code>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <customer-company-name v-model="houseInsuranceOwnerForm.company_name"
                                             :customer-index="0"
                                             @validation-customer-status="handleValidationCustomerStatus"></customer-company-name>
                    </v-col>
                  </v-row>
                  <v-row class="mt-5">
                    <v-col cols="12" sm="12" md="12">
                      <customer-caen-code-select v-model="houseInsuranceOwnerForm.caen_code"
                                                 :customer-index="0"
                                                 @validation-customer-status="handleValidationCustomerStatus"></customer-caen-code-select>
                    </v-col>
                  </v-row>
                  <v-row class="my-3" v-if="!user">
                    <v-col cols="12" sm="12" md="6">
                      <customer-email v-model="houseInsuranceOwnerForm.email"
                                      :customer-index="0"
                                      @validation-customer-status="handleValidationCustomerStatus"></customer-email>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <customer-phone-number v-model="houseInsuranceOwnerForm.phone_number"
                                             :customer-index="0"
                                             @validation-customer-status="handleValidationCustomerStatus"></customer-phone-number>
                    </v-col>
                  </v-row>
                </div>

                <div>
                  <v-row>
                    <v-col cols="12">
                      <h3 class="font-weight-bold">
                        {{ translation(translationList.owner_address_data, 'Adresa proprietarului') }}</h3>
                      <p class="font-weight-regular">
                        {{
                          translation(translationList.owner_address_data_helper, 'Completați câmpurile următoare cu adresa exactă.')
                        }}
                      </p>
                    </v-col>
                  </v-row>

                  <v-row class="my-3">
                    <v-col cols="12" sm="12" md="6">
                      <address-county-select v-model="houseInsuranceOwnerForm.address.county"
                                             @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-county-select>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <address-city-select
                          v-model="houseInsuranceOwnerForm.address.city"
                          @validation-address-status="handleValidationHouseOwnerAddressStatus"
                          :county-id="houseInsuranceOwnerForm.address.county?.id"
                      ></address-city-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="4">
                      <address-street-type-select v-model="houseInsuranceOwnerForm.address.street_type"
                                                  @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-street-type-select>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-street v-model="houseInsuranceOwnerForm.address.street"
                                      @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-street>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-street-number v-model="houseInsuranceOwnerForm.address.street_number"
                                             @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-street-number>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="4">
                      <address-building v-model="houseInsuranceOwnerForm.address.building"
                                        @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-building>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-entrance v-model="houseInsuranceOwnerForm.address.entrance"
                                        @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-entrance>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                      <address-floor v-model="houseInsuranceOwnerForm.address.floor"
                                     @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-floor>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" md="6">
                      <address-apartment v-model="houseInsuranceOwnerForm.address.apartment"
                                         @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-apartment>
                    </v-col>
                    <v-col cols="12" sm="12" md="6">
                      <address-postal-code v-model="houseInsuranceOwnerForm.address.postal_code"
                                           @validation-address-status="handleValidationHouseOwnerAddressStatus"></address-postal-code>
                    </v-col>
                  </v-row>
                </div>

                <v-row class="mt-5">
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-start centered-on-mobile">
                    <v-btn
                        class="block-on-mobile"
                        color="primary-light"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="backToStep(1)"
                    >
                      {{ translation(translationList.step, 'Pasul') }}
                      {{ translation(translationList.previous, 'Anterior') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-end centered-on-mobile">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="validateStep(2)"
                    >
                      {{ translation(translationList.step, 'Pasul') }}
                      {{ translation(translationList.next, 'Următor') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </template>

            <template v-slot:item.3>
              <div>
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <house-offer-type-tab v-model="houseInsuranceForm.policy_type"></house-offer-type-tab>
                  </v-col>
                </v-row>

                <v-row v-if="houseInsuranceForm.policy_type?.name === 'Facultativa'">
                  <v-col cols="12">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.pad_information, 'Informații suplimentare') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.pad_information_helper, 'Completează seria și numărul poliței PAD.')
                      }}
                    </p>
                  </v-col>
                </v-row>

                <v-row v-if="houseInsuranceForm.policy_type?.name === 'Facultativa'">
                  <v-col cols="12" sm="12" md="6">
                    <house-pad-series v-model="houseInsuranceForm.pad_series"
                                      @validation-house-status="handleValidationHouseStatus"></house-pad-series>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <house-pad-number v-model="houseInsuranceForm.pad_number"
                                      @validation-house-status="handleValidationHouseStatus"></house-pad-number>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.insurance_valid_from, 'Data de început a asigurării') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.insurance_valid_from_helper, 'Alege ziua în care vrei să înceapă acoperirea asigurării.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <house-insurance-valid-from v-model="houseInsuranceForm.valid_from"
                                                :disabled="isValidFromDisabled"></house-insurance-valid-from>
                  </v-col>
                </v-row>
                <v-row class="mt-5">
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-start centered-on-mobile">
                    <v-btn
                        class="block-on-mobile"
                        color="primary-light"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="backToStep(2)"
                    >
                      {{ translation(translationList.step, 'Pasul') }}
                      {{ translation(translationList.previous, 'Anterior') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex justify-end centered-on-mobile">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="validateStep(3)"
                    >
                      {{ translation(translationList.step, 'Pasul') }}
                      {{ translation(translationList.next, 'Următor') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </template>

            <template v-slot:item.4>
              <div>
                <v-row class="my-5">
                  <v-col cols="12">
                    <div class="fw-400 fs-20">
                      {{
                        translation(translationList.perfect_choice_house, 'Perfect! Iată cele mai bune oferte pentru locuința ta. Compară și alege opțiunea dorită.')
                      }}
                    </div>
                  </v-col>
                </v-row>

                <div v-if="offers && Object.keys(offers).length !== 0">
                  <v-expansion-panels color="primary-light" v-model="activePanels" rounded="0" variant="accordion"
                                      class="mt-3">
                    <v-expansion-panel eager class="w-100" v-for="(insurerOffers, insurerId) in offers"
                                       :key="insurerId">
                      <v-expansion-panel-title style="min-height: 175px;" v-if="insurerOffers.length > 0"
                                               class="border-expansion">
                        <template v-slot:default="{ expanded }">
                          <v-row class="py-3 px-3 d-flex align-center justify-center">
                            <v-col cols="12" sm="6" md="3">
                              <NuxtImg
                                  :src="'/images/companies/insurance/thumbnails/' + insurerOffers[0][0].house_insurance_company.code + '.webp'"
                                  :alt="insurerOffers[0][0].house_insurance_company.name" class="pa-1 w-100"
                              />
                            </v-col>
                            <v-col cols="12" sm="12" md="9">
                              <v-row>
                                <v-col cols="4" sm="4" md="4" class="text-center fs-16 fw-300">
                                  {{ translation(translationList.house_available_offers, 'Număr oferte disponibile') }}
                                </v-col>
                                <v-col cols="4" sm="4" md="4" class="text-center fs-16 fw-300">
                                  {{ translation(translationList.house_cheapest_offer, 'Cea mai ieftină ofertă') }}
                                </v-col>
                                <v-col cols="4" sm="4" md="4" class="text-center fs-16 fw-300">
                                  {{ translation(translationList.house_expensive_offer, 'Cea mai scumpă ofertă') }}
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="4" sm="4" md="4" class="text-center fs-16 fw-700">
                                  {{ getOffersLength(insurerOffers) }} {{ translation(translationList.offers, 'Oferte') }}
                                </v-col>

                                <v-col cols="4" sm="4" md="4" class="text-center fs-16 fw-700">
                                  {{ getLowestAmount(insurerOffers) }} RON
                                </v-col>

                                <v-col cols="4" sm="4" md="4" class="text-center fs-16 fw-700">
                                  {{ getHighestAmount(insurerOffers) }} RON
                                </v-col>

                              </v-row>
                            </v-col>
                          </v-row>
                        </template>
                      </v-expansion-panel-title>

                      <v-expansion-panel-text class="border-expansion">
                        <v-row class="my-2">
                          <template v-for="(offerGroup) in insurerOffers" :key="offerGroup.id">
                            <v-col cols="12" sm="6" md="4" class="pa-4">
                              <v-hover>
                                <template v-slot:default="{ isHovering, props }">
                                  <v-card v-bind="props" variant="outlined" class="border-disabled-dark" rounded="0"
                                          min-height="175" min-width="175">
                                    <NuxtImg
                                        :src="offerGroup[0].type_name === 'Facultativa' ? '/images/house/house-insurance-1.webp' : '/images/house/house-insurance-2.webp'"
                                        :alt="offerGroup[0].type_name" class="w-100"
                                    />

                                    <div class="pa-3 fs-18 fw-600 d-flex flex-column align-center">
                                      <div>{{ offerGroup[0].house_insurance_product.name }}</div>
                                      <div>{{ offerGroup[0].house_insurance_package.name }}</div>
                                    </div>

                                    <v-divider></v-divider>

                                    <v-card-text class="fs-16">
                                      <v-card-subtitle>Cod:
                                        {{ offerGroup.map(offer => offer.insurer_offer_code).join(' + ') }}
                                      </v-card-subtitle>
                                      <v-list>
                                        <v-list-item>
                                          <v-list-item-title class="fs-16 fw-600">
                                            {{ translation(translationList.offer_type, 'Tip ofertă') }}
                                          </v-list-item-title>
                                          <v-list-item-subtitle>{{
                                              offerGroup.map(offer => offer.type_name).join(' + ')
                                            }}
                                          </v-list-item-subtitle>
                                        </v-list-item>
                                        <v-list-item>
                                          <v-list-item-title class="fs-16 fw-600">
                                            {{ translation(translationList.valid_from, 'Valabil de la') }}
                                          </v-list-item-title>
                                          <v-list-item-subtitle>{{ offerGroup[0].valid_from }}</v-list-item-subtitle>
                                        </v-list-item>
                                        <v-list-item>
                                          <v-list-item-title class="fs-16 fw-600">
                                            {{ translation(translationList.valid_until, 'Valabil până la') }}
                                          </v-list-item-title>
                                          <v-list-item-subtitle>{{ offerGroup[0].valid_until }}</v-list-item-subtitle>
                                        </v-list-item>
                                      </v-list>
                                    </v-card-text>


                                    <v-card rounded="0" color="primary-light" flat>
                                      <v-row no-gutters>
                                        <v-col cols="12" sm="12" :md="offerGroup[1] ? '6' : '12'">
                                          <v-card-text class="text-center">
                                            <div class="fw-400 fs-14">
                                              {{ offerGroup[0].type_name }}
                                            </div>
                                            <div class="fw-700 fs-18">
                                              {{ offerGroup[0].amount_to_be_payed }}RON
                                            </div>
                                          </v-card-text>
                                        </v-col>
                                        <v-col cols="12" sm="12" md="6" v-if="offerGroup[1]">
                                          <v-card-text class="text-center">
                                            <div class="fw-400 fs-14">
                                              {{ offerGroup[1].type_name }}
                                            </div>
                                            <div class="fw-700 fs-18">
                                              {{ offerGroup[1].amount_to_be_payed }}RON
                                            </div>
                                          </v-card-text>
                                        </v-col>
                                      </v-row>
                                    </v-card>

                                    <v-card rounded="0" color="primary-accent" flat>
                                      <v-card-text class="text-center">
                                        <div class="fw-400 fs-20">
                                          Total:
                                        </div>
                                        <div class="fw-700 fs-24">
                                          {{
                                            offerGroup.reduce((sum, offer) => sum + parseFloat(offer.amount_to_be_payed), 0).toFixed(2)
                                          }} RON
                                        </div>
                                      </v-card-text>
                                    </v-card>

                                    <div v-if="isHovering" class="overlay cursor-pointer"></div>
                                  </v-card>
                                </template>
                              </v-hover>
                            </v-col>
                          </template>
                        </v-row>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
                <div v-if="isLoadingSkeleton">
                  <v-skeleton-loader type="image"></v-skeleton-loader>
                </div>
                <div v-if="!isLoadingSkeleton && Object.keys(offers).length === 0">
                  <v-card
                      rounded="0"
                      elevation="0"
                      height="180"
                  >
                    <v-card-text class="fw-500 fs-16">
                      <v-icon color="primary" size="x-large">mdi-information</v-icon>
                      {{ translation(translationList.house_offer_not_available, 'Nu există oferte disponibile.') }}
                    </v-card-text>
                  </v-card>
                </div>
                <v-row v-show="!isLoadingSkeleton" class="mt-10 mb-15">
                  <v-col cols="12" class="d-flex align-center justify-center">
                    <v-btn
                        class="block-on-mobile"
                        color="primary-light"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="backToStep(3)"
                    >
                      {{ translation(translationList.step, 'Pasul') }}
                      {{ translation(translationList.previous, 'Anterior') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </template>
          </v-stepper>
        </v-container>
      </v-container>
    </v-container>
  </master-layout>
</template>

<style scoped>

.goods-list-item:hover {
  background-color: rgba(255, 0, 0, 0.1); /* Overlay roșu cu opacitate */
}

.goods-list-item-title {
  position: relative;
}

.delete-icon {
  display: none;
  cursor: pointer;
  color: red;
}

.goods-list-item-title:hover .delete-icon {
  display: grid;
}

</style>